import { consola } from 'consola'
import { isPaidPaymentStatus } from '../lib/ordersPaidSinceLastPoll'
import { usePaymentStore } from './usePaymentStore'
import { apiOrder, useCartStore } from '@entities/order'
import { useAnalytics } from '@shared/lib/analytics'

const logger = consola.withTag('payment-reconcile')


/**
 * Заказы, по которым корзина уже очищена в этой вкладке. Живёт на уровне модуля,
 * чтобы переживать перемонтирование композабла и не чистить корзину повторно.
 */
const reconciledOrderIds = new Set<string>()

/** По каким заказам успех оплаты уже улетел в аналитику — переживает перезагрузку. */
const PURCHASE_TRACKED_KEY = 'analytics_purchase_tracked'

/**
 * Единственная точка события «оплата прошла»: здесь, а не на /success, потому что
 * редирект на /success — только UX-триггер, истина об оплате — статус из webhook.
 * Один заказ — одно событие: и последний шаг воронки заказа, и ecommerce-purchase
 * Метрики задваивались бы на каждом повторном заходе на страницу успеха.
 */
const trackPurchaseOnce = (orderId: string): void => {
  try {
    const raw = localStorage.getItem(PURCHASE_TRACKED_KEY)
    const tracked: string[] = raw ? JSON.parse(raw) : []
    if (tracked.includes(orderId)) return
    localStorage.setItem(PURCHASE_TRACKED_KEY, JSON.stringify([...tracked, orderId].slice(-20)))
  } catch {
    // Хранилище недоступно — дедуп в пределах вкладки обеспечивает reconciledOrderIds.
  }

  useAnalytics().track('payment_result', { orderId: Number(orderId) || undefined, ok: true })
}

const isPaid = isPaidPaymentStatus

/**
 * Единая точка «сверки оплаты» для клиента.
 *
 * Источник истины об оплате — статус заказа на бэке (его выставляет server-to-server
 * webhook провайдера). Redirect на /success, callback виджета и deeplink НЕнадёжны и
 * служат лишь триггерами этой сверки. Здесь мы проверяем статус заказа и при успешной
 * оплате один раз очищаем клиентскую корзину (серверную чистит листенер на бэке).
 *
 * Очистка привязана к КОНКРЕТНОМУ orderId (тому, что оплачивали), а не к «любому
 * оплаченному заказу» — чтобы не стереть корзину, которую пользователь начал собирать заново.
 *
 * Сторы берём лениво внутри методов (а не на setup), чтобы композабл можно было создавать
 * вне Pinia-контекста (например, в составе другого композабла под юнит-тестом).
 */
export function usePaymentReconciliation() {
  /**
   * Сверяет статус заказа с бэком и при успешной оплате очищает корзину (один раз, идемпотентно).
   * @param rawOrderId id заказа; если не передан — берётся текущий заказ из payment store.
   * @returns true, если заказ оплачен.
   */
  async function reconcilePaidOrder(rawOrderId?: string | number | null): Promise<boolean> {
    try {
      const paymentStore = usePaymentStore()

      let orderId: string | null = null
      if (rawOrderId != null && rawOrderId !== '') orderId = String(rawOrderId)
      else if (paymentStore.orderId != null) orderId = String(paymentStore.orderId)

      if (!orderId) {
        logger.debug('Пропуск сверки: нет orderId')
        return false
      }

      // Уже сверяли и чистили по этому заказу — повторно не дёргаем API и корзину
      if (reconciledOrderIds.has(orderId)) return true

      const { status, payment_status: paymentStatus } = await apiOrder.getOrder(orderId)

      if (!isPaid(paymentStatus)) {
        logger.debug('Заказ ещё не оплачен', { orderId, paymentStatus, status })
        return false
      }

      reconciledOrderIds.add(orderId)
      logger.info('Заказ оплачен → очищаем клиентскую корзину', { orderId })
      paymentStore.setStatus('succeeded')
      trackPurchaseOnce(orderId)

      try {
        await useCartStore().clearCart()
      } catch (error) {
        // Корзина могла быть уже пустой/очищенной — это не ошибка сверки
        logger.warn('clearCart при сверке не удался (возможно, уже пуста)', error)
      }

      return true
    } catch (error) {
      logger.warn('Сверка оплаты не удалась', error)
      return false
    }
  }

  /**
   * Сверка с короткими ретраями: redirect/возврат может прилететь раньше, чем webhook
   * провайдера выставит статус заказа. Поллим статус несколько раз, пока не увидим оплату.
   */
  async function reconcileWithRetry(
    rawOrderId?: string | number | null,
    opts?: { attempts?: number; intervalMs?: number },
  ): Promise<boolean> {
    const attempts = opts?.attempts ?? 10
    const intervalMs = opts?.intervalMs ?? 3000

    for (let i = 0; i < attempts; i++) {
      if (await reconcilePaidOrder(rawOrderId)) return true
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs))
      }
    }
    return false
  }

  /**
   * Фоновый отменяемый поллинг статуса заказа до факта оплаты.
   *
   * Нужен для модалок оплаты: коллбэк виджета (T-Business `changedCallback`, ЮKassa
   * `complete`) не срабатывает, когда пользователь платит по fallback-ссылке в другой
   * вкладке/банковском приложении. Источник истины — статус заказа (его выставляет
   * webhook провайдера), поэтому просто опрашиваем его, пока не увидим оплату, и тогда
   * закрываем модалку через {@link onPaid} (корзину чистит сама сверка, идемпотентно).
   *
   * @returns функция остановки — обязательно вызвать на unmount модалки.
   */
  function pollUntilPaid(
    rawOrderId: string | number | null | undefined,
    onPaid: () => void,
    opts?: { intervalMs?: number; timeoutMs?: number },
  ): () => void {
    const intervalMs = opts?.intervalMs ?? 3000
    // 15 минут: оплата по ссылке в другой вкладке/приложении может занять время.
    const timeoutMs = opts?.timeoutMs ?? 900_000

    let stopped = false
    let intervalId: ReturnType<typeof setInterval> | null = null
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const stop = (): void => {
      if (stopped) return
      stopped = true
      if (intervalId != null) clearInterval(intervalId)
      if (timeoutId != null) clearTimeout(timeoutId)
    }

    const tick = async (): Promise<void> => {
      if (stopped) return
      const paid = await reconcilePaidOrder(rawOrderId)
      if (stopped) return
      if (paid) {
        stop()
        onPaid()
      }
    }

    // Первая проверка сразу (вдруг уже оплачено), далее — по интервалу до таймаута.
    void tick()
    intervalId = setInterval(() => void tick(), intervalMs)
    timeoutId = setTimeout(stop, timeoutMs)

    return stop
  }

  return { reconcilePaidOrder, reconcileWithRetry, pollUntilPaid }
}
